import { dateFromObjectId, Switch } from "@xola/ui-kit";
import { formatDate, isMistPurchase, isStoreCreditPurchaseItem } from "common";
import dayjs, { Dayjs } from "dayjs";
import { sortBy } from "lodash";
import React, { useMemo, useState } from "react";
import { useQueries } from "react-query";
import {
    Audit,
    Conversation,
    Gratuity,
    LineItem,
    LineItemType,
    Message,
    PaymentLineItem,
    Purchase,
    PurchaseItem,
} from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useCollection } from "../../../hooks/useCollection";
import { useResource } from "../../../hooks/useResource";
import { isGratuityEnabled } from "../../../models/Seller";
import { AddChargeTimelineItem } from "./Items/AddChargeTimelineItem";
import { AddOnTimelineItem } from "./Items/AddOnTimelineItem";
import { AdjustBalanceTimelineItem } from "./Items/AdjustBalanceTimeLineItem";
import { ArrivalTimelineItem } from "./Items/ArrivalTimelineItem";
import { CancellationProtectionTimelineItem } from "./Items/CancellationProtectionTimelineItem";
import { CancelTimelineItem } from "./Items/CancelTimelineItem";
import { ChangeChargeTimelineItem } from "./Items/ChangeChargeTimelineItem";
import { ChangeQuantityItem } from "./Items/ChangeQuantityItem";
import { CreatedTimelineItem } from "./Items/CreatedTimelineItem";
import { DisputeTimelineItem } from "./Items/DisputeTimelineItem";
import { FundsReversedTimelineItem } from "./Items/FundsReversedTimelineItem";
import { GratuityTimelineItem } from "./Items/GratuityTimelineItem";
import { GuestStatusChangedItem } from "./Items/GuestStatusChangedItem";
import { MessageTimelineItem } from "./Items/MessageTimelineItem";
import { OrderFulfilledTimelineItem, OrderPartiallyFulfilledTimelineItem } from "./Items/OrderFulfillmentTimelineItem";
import { PackageSplitTimelineItem } from "./Items/PackageSplitTimelineItem";
import { PartnerCommissionTimelineItem } from "./Items/PartnerCommissionTimelineItem";
import { PartnerTimelineItem } from "./Items/PartnerTimelineItem";
import { PaymentTimelineItem } from "./Items/PaymentTimelineItem";
import { PickupLocationItem } from "./Items/PickupLocationItem";
import { PrivacyChangeLineItem } from "./Items/PrivacyChangeLineItem";
import { QuestionnaireUpdatedLineItem } from "./Items/QuestionnaireUpdatedLineItem";
import { RedeemLineItem } from "./Items/RedeemLineItem";
import { RefundTimelineItem } from "./Items/RefundTimelineItem";
import { RemoveChargeTimelineItem } from "./Items/RemoveChargeLineItem";
import { RemoveCouponTimelineItem } from "./Items/RemoveCouponTimelineItem";
import { ReturnPaymentTimelineItem } from "./Items/ReturnPaymentTimelineItem";
import { AcceptedTimelineItem, DeclinedTimelineItem } from "./Items/StatusTimelineItem";
import { SwitchExperienceTimelineItem } from "./Items/SwitchExperienceTimelineItem";
import { TaxFeeChangeTimelineItem } from "./Items/TaxFeeChangeTimelineItem";
import { WaiverSignedTimelineItem } from "./Items/WaiverSignedTimelineItem";
import { api } from "@/api/api";
import { applyFulfillmentAuditGrouping } from "@/components/PurchasesMenu/Timeline/fulfillmentAuditGroupingHelper";
import { CodeRedeemLineItem } from "@/components/PurchasesMenu/Timeline/Items/CodeRedeemLineItem";
import { DiscountCodeTimelineItem } from "@/components/PurchasesMenu/Timeline/Items/DiscountCodeTimelineItem";
import { GratuityModifiedTimelineItem } from "@/components/PurchasesMenu/Timeline/Items/GratuityModifiedTimelineItem";
import { GratuityTransferTimelineItem } from "@/components/PurchasesMenu/Timeline/Items/GratuityTransferTimelineItem";
import { GuestCheckInItem } from "@/components/PurchasesMenu/Timeline/Items/GuestCheckInItem";
import { applyQuantityChangeGrouping } from "@/components/PurchasesMenu/Timeline/quantityChangeGroupingHelper";
import { useMessageDeliveryReports } from "@/hooks/resources/useMessageDeliveryReports";
import { getAuditData } from "@/models/Audit";

const useWaiverAudits = (waiverIds: ID[] = []) => {
    const uniqueWaiverIds = [...new Set(waiverIds)].filter(Boolean);

    const waiverQueries = useQueries(
        uniqueWaiverIds.map((waiverId) => {
            const params = { objectId: waiverId, objectClass: "waiver" };

            return {
                queryKey: ["audits", params],
                queryFn: async () => {
                    const { data } = await api.get<Audit>("/audits", { params });
                    return data.data || [];
                },
                staleTime: 3000,
                refetchOnWindowFocus: false,
                enabled: !!waiverId,
            };
        }),
    );

    const isLoading = waiverQueries.some((query) => query.isLoading);
    const isError = waiverQueries.some((query) => query.isError);
    const error = waiverQueries.find((query) => query.error)?.error;
    const data = waiverQueries.filter((query) => query.data).flatMap((query) => query.data);

    return {
        data,
        isLoading,
        isError,
        error,
    };
};

export const PurchaseTimeline = ({ purchase, item }: { purchase: Purchase; item?: PurchaseItem }) => {
    const seller = useSeller();
    const [showReservationEvents, setShowReservationEvents] = useState(true);
    const [showCommunication, setShowCommunication] = useState(true);
    const { data: audits } = useCollection<Audit>(
        "audits",
        { objectId: purchase.id, objectClass: "purchase", seller: purchase.seller.id },
        { staleTime: 3000 },
    );

    const itemIds = purchase.items
        .filter((item) => {
            // Exclude items that have gratuity line items since we will have audits for them already
            return !item.lineItems.some((lineItem) => lineItem.type === LineItemType.GRATUITY);
        })
        .map((item) => item.id)
        .toString();

    const gratuityParams = { "references[in]": itemIds, expand: "transaction" };

    const { data: gratuityItems } = useCollection<Gratuity>("gratuities", gratuityParams, {
        enabled: isGratuityEnabled(seller) && itemIds.length > 0,
    });

    const { data: conversation } = useResource<Conversation>(["conversations", purchase.conversation?.id], null, {
        refetchInterval: 30_000,
    });

    // Extract waiver IDs from purchase items with clean memoization
    // Fetch waiver audits only if the waiver is modified
    const waiverIds = useMemo(() => {
        const uniqueWaiverIds = new Set<string>();
        for (const item of purchase.items) {
            for (const waiver of item.waivers ?? []) {
                if (waiver.createdAt && waiver.updatedAt) {
                    if (dayjs(waiver.updatedAt).isAfter(waiver.createdAt)) {
                        uniqueWaiverIds.add(waiver.id);
                    }
                } else {
                    // If waiver does not have createdAt or updatedAt, we fetch the audits
                    uniqueWaiverIds.add(waiver.id);
                }
            }
        }
        return [...uniqueWaiverIds];
    }, [purchase.items]);

    const { data: waiverAudits = [] } = useWaiverAudits(waiverIds);

    const updatedAt = conversation?.updatedAt ? conversation?.updatedAt : null;
    const formedMessages =
        conversation?.messages?.map((message: Message) => {
            return message.updatedAt ? { ...message } : { ...message, updatedAt };
        }) ?? [];

    const selectedItem = item ?? purchase.items[0];
    const isMISTRoot = isMistPurchase(purchase) && !item;

    // Combine all audit data
    const allAudits = [...(audits || []), ...waiverAudits];

    const reservationEvents = showReservationEvents
        ? [...gratuityItems, ...purchase.lineItems, ...selectedItem.lineItems, ...allAudits]
        : [];

    const communicationEvents = showCommunication ? [...(formedMessages as Message[])] : [];
    const { reportsByMessage } = useMessageDeliveryReports(purchase.conversation?.id, communicationEvents);
    const items = item ? [item] : purchase.items;

    const sortPayload = [...reservationEvents, ...communicationEvents];
    if (isMISTRoot) {
        // If this is the root of a MIST purchase we need to add the line items from the items
        const itemEvents = purchase.items.map((item) => item.lineItems);
        sortPayload.push(...itemEvents.flat());
    }

    const changes = getSortedChanges(sortPayload, purchase, item);

    let currentDate = "";

    return (
        <div className="py-10">
            <div className="mb-8 flex flex-row justify-between">
                <h2>Timeline</h2>
                <div>
                    <Switch.Group>
                        <div data-testid="purchase-timeline-detail-toggle-reservation">
                            <Switch
                                isChecked={showReservationEvents}
                                size="medium"
                                onChange={() => setShowReservationEvents(!showReservationEvents)}
                            />

                            <Switch.Label direction="left" className="ml-3 font-bold">
                                Reservation Events
                            </Switch.Label>
                        </div>
                    </Switch.Group>
                    {formedMessages.length > 0 ? (
                        <Switch.Group>
                            <div data-testid="purchase-timeline-detail-toggle-communication">
                                <Switch
                                    isChecked={showCommunication}
                                    size="medium"
                                    onChange={() => setShowCommunication(!showCommunication)}
                                />

                                <Switch.Label direction="left" className="ml-3 font-bold">
                                    Communication
                                </Switch.Label>
                            </div>
                        </Switch.Group>
                    ) : null}
                </div>
            </div>

            {changes.map((change, changeIndex: number) => {
                if ("from" in change && change.from) {
                    return messageTiles.map((Tile) => (
                        <Tile
                            key={change.id}
                            purchase={purchase}
                            item={selectedItem}
                            message={change}
                            deliveryReports={reportsByMessage[change.id]}
                        />
                    ));
                }

                return auditTiles.map((Tile, auditIndex) => {
                    // Go through every tile in our list and check if it supports this audit by calling `.supportsAudit`
                    // "support" means it can render a tile for this audit

                    const createdAt = "createdAt" in change ? formatDate(change.createdAt) : "";
                    const showDate = currentDate === "" || currentDate !== createdAt || changeIndex === 0;

                    // Handling MIST purchase page timeline items
                    if (isMISTRoot && isSupportsAudit(Tile, purchase, selectedItem, change, seller.timezoneName)) {
                        // These will generally be payment related items
                        currentDate = createdAt;

                        return (
                            <TimelineItemTile
                                // eslint-disable-next-line react/no-array-index-key
                                key={auditIndex}
                                Tile={Tile}
                                showDate={showDate}
                                purchase={purchase}
                                item={selectedItem}
                                change={change}
                            />
                        );
                    }

                    if (
                        "fundingSource" in change &&
                        change.fundingSource &&
                        isStoreCreditPurchaseItem(selectedItem) &&
                        isSupportsAudit(Tile, purchase, selectedItem, change, seller.timezoneName)
                    ) {
                        return (
                            <TimelineItemTile
                                // eslint-disable-next-line react/no-array-index-key
                                key={auditIndex}
                                Tile={Tile}
                                showDate={showDate}
                                purchase={purchase}
                                item={selectedItem}
                                change={change}
                            />
                        );
                    }

                    return items.map((currentItem, index) => {
                        let itemForAudit = selectedItem;
                        if (isMISTRoot) {
                            // For root purchases we need to show timeline tiles for all items
                            itemForAudit = purchase.items.find((item) => item.id === currentItem.id) ?? selectedItem;
                        }

                        if (isSupportsAudit(Tile, purchase, itemForAudit, change, seller.timezoneName)) {
                            currentDate = createdAt;
                            // Since this Tile supports this audit, render it
                            return (
                                <TimelineItemTile
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={auditIndex + index}
                                    Tile={Tile}
                                    showDate={showDate}
                                    purchase={purchase}
                                    item={currentItem}
                                    change={change}
                                />
                            );
                        }

                        return null;
                    });
                });
            })}

            {showReservationEvents ? (
                <CreatedTimelineItem showDate purchase={purchase} item={selectedItem} audits={allAudits} />
            ) : null}
        </div>
    );
};

const auditTiles = [
    AcceptedTimelineItem,
    ChangeChargeTimelineItem,
    RemoveChargeTimelineItem,
    AddChargeTimelineItem,
    AdjustBalanceTimelineItem,
    PartnerCommissionTimelineItem,
    ArrivalTimelineItem,
    CancelTimelineItem,
    OrderFulfilledTimelineItem,
    OrderPartiallyFulfilledTimelineItem,
    ChangeQuantityItem,
    DiscountCodeTimelineItem,
    RemoveCouponTimelineItem,
    DeclinedTimelineItem,
    GuestStatusChangedItem,
    GuestCheckInItem,
    PaymentTimelineItem,
    GratuityTimelineItem,
    GratuityTransferTimelineItem,
    GratuityModifiedTimelineItem,
    RefundTimelineItem,
    ReturnPaymentTimelineItem,
    SwitchExperienceTimelineItem,
    PackageSplitTimelineItem,
    WaiverSignedTimelineItem,
    PickupLocationItem,
    AddOnTimelineItem,
    RedeemLineItem,
    CodeRedeemLineItem,
    PrivacyChangeLineItem,
    QuestionnaireUpdatedLineItem,
    DisputeTimelineItem,
    TaxFeeChangeTimelineItem,
    PartnerTimelineItem,
    FundsReversedTimelineItem,
    CancellationProtectionTimelineItem,
];

const messageTiles = [MessageTimelineItem];

interface TimelineItemTileProps {
    Tile: typeof auditTiles[0];
    purchase: Purchase;
    showDate: boolean;
    item: PurchaseItem;
    change: any;
    purchaseCommitedAt?: Dayjs;
}

const TimelineItemTile = ({ Tile, purchase, item, showDate, change, purchaseCommitedAt }: TimelineItemTileProps) => {
    const audit = change.data ? getAuditData(change) : { ...change, data: {} };
    const lineItem = change.data ? null : change;

    return (
        <Tile
            showDate={showDate}
            purchase={purchase}
            item={item}
            audit={audit}
            lineItem={lineItem}
            change={change}
            purchaseCommitedAt={purchaseCommitedAt}
        />
    );
};

const getSortedChanges = (changes: any[], purchase: Purchase, item?: PurchaseItem) => {
    const changesWithSortField = changes.map((change) => {
        let newElement = { ...change };

        if (change.createdAt) {
            newElement = {
                ...newElement,
                fieldToSortByDate: dayjs(change.createdAt),
            };
            return newElement;
        }

        newElement = {
            ...newElement,
            fieldToSortByDate: dayjs(dateFromObjectId(change.id)),
        };
        return newElement;
    });

    const sortedChangesByDate = sortBy(changesWithSortField, "fieldToSortByDate").reverse() ?? [];

    const swappedChanges = sortedChangesByDate.sort((a, b) => {
        const auditOne = a.data ? getAuditData(a) : { ...a, data: {} };
        const auditTwo = b.data ? getAuditData(b) : { ...b, data: {} };

        const shouldBeSwappedReturnItemWithRefund =
            ReturnPaymentTimelineItem.supportsAudit(purchase, item!, auditTwo) &&
            RefundTimelineItem.supportsAudit(purchase, item!, auditOne);

        if (shouldBeSwappedReturnItemWithRefund) {
            return 1;
        }

        if (a.fieldToSortByDate?.isSame?.(b.fieldToSortByDate)) {
            // Same second can happen for bulk audit writes; use ObjectId recency as tie-breaker.
            return String(b.id ?? "").localeCompare(String(a.id ?? ""));
        }

        return 0;
    });

    const groupedByQuestionnaire = applyQuestionnaireGrouping(swappedChanges, purchase, item);
    const groupedByFulfillment = applyFulfillmentAuditGrouping(groupedByQuestionnaire, purchase, item);
    return applyQuantityChangeGrouping(groupedByFulfillment, purchase, item);
};

const isSupportsAudit = (
    Tile: typeof auditTiles[0],
    purchase: Purchase,
    item: PurchaseItem,
    change: any,
    timezone: string,
) => {
    if (change.objectClass === "waiver" && Tile === WaiverSignedTimelineItem) {
        return true;
    }

    const audit = change.data ? getAuditData(change) : { ...change, data: {} };

    const lineItem = change.data ? null : change;
    return Tile.supportsAudit(purchase, item, audit, lineItem, timezone, change);
};

// Group questionnaire updates that happen within a short time frame into a single timeline item
// to prevent multiple questionnaire updates flooding the timeline.
const applyQuestionnaireGrouping = (sortedList: TimelineChange[], purchase: Purchase, item?: PurchaseItem) => {
    const result: TimelineChange[] = [];
    const QUESTIONNAIRE_GROUPING_THRESHOLD_SECONDS = 60;

    let lastQuestionnaireTime: Dayjs | null = null;
    let lastQuestionnaireUserId: string | null = null;

    for (const change of sortedList) {
        const isQuestionnaire = isQuestionnaireAudit(change, purchase, item);

        if (!isQuestionnaire) {
            result.push(change);
            lastQuestionnaireTime = null;
            lastQuestionnaireUserId = null;
            continue;
        }

        const currentTime = getChangeTime(change);
        const currentUserId = getAuditUserId(change);

        if (lastQuestionnaireTime) {
            const diffInSeconds = lastQuestionnaireTime.diff(currentTime, "second");
            const isSameUser = lastQuestionnaireUserId === currentUserId;
            const hasUser = !!currentUserId;

            if (diffInSeconds <= QUESTIONNAIRE_GROUPING_THRESHOLD_SECONDS && isSameUser && hasUser) {
                lastQuestionnaireTime = currentTime;
                continue;
            }
        }

        result.push(change);
        lastQuestionnaireTime = currentTime;
        lastQuestionnaireUserId = currentUserId;
    }

    return result;
};

type TimelineChange = Audit | Message | Gratuity | LineItem | PaymentLineItem;

const getChangeTime = (change: TimelineChange) => {
    return change.createdAt ? dayjs(change.createdAt) : dayjs(dateFromObjectId(change.id));
};

const getAuditUserId = (change: TimelineChange) => {
    const audit = "data" in change && change.data ? getAuditData(change) : { ...change, data: {} };
    return audit?.createdBy?.id;
};

const isQuestionnaireAudit = (change: TimelineChange, purchase: Purchase, purchaseItem?: PurchaseItem) => {
    if (!purchaseItem) {
        return false;
    }

    const audit = "data" in change && change.data ? getAuditData(change) : { ...change, data: {} };
    return (
        QuestionnaireUpdatedLineItem.supportsAudit(purchase, purchaseItem, audit as Audit) &&
        !WaiverSignedTimelineItem.supportsAudit(purchase, purchaseItem, audit as Audit)
    );
};
